import { useState, useMemo } from 'react';
import './PriceChangeModal.css';

const MAX_DAYS = 30;

/** Round up (ceiling) to nearest integer. */
function ceilPrice(v) {
  return Math.ceil(Number(v));
}

/**
 * Compute per-day concrete prices for gradual change.
 * Returns array of length `days`, index 0 = day 1 (tomorrow), etc.
 */
function computeSchedulePrices(currentPrice, targetPrice, days) {
  const step = (targetPrice - currentPrice) / days;
  return Array.from({ length: days }, (_, i) =>
    ceilPrice(currentPrice + step * (i + 1))
  );
}

/** Format date as YYYY-MM-DD offset from today by +dayOffset days. */
function formatDate(dayOffset) {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

/**
 * PriceChangeModal
 *
 * Props:
 *   products     — all loaded products [{offer_id, name}]
 *   priceMap     — { offer_id: { price, ... } }
 *   selectedIds  — Set of pre-selected offer_ids (from checkbox selection in table)
 *   onClose      — () => void
 *   onApplyNow   — (prices: [{offer_id, price}]) => Promise
 *   onSchedule   — (items: [{offer_id, name, current_price, target_price}], days: number) => Promise
 */
function PriceChangeModal({ products, priceMap, selectedIds, onClose, onApplyNow, onSchedule }) {
  // Selection scope
  const [scope, setScope] = useState(selectedIds?.size > 0 ? 'selected' : 'all');

  // Change type: 'percent' | 'absolute'
  const [changeType, setChangeType] = useState('percent');
  // Direction: 'up' | 'down'
  const [direction, setDirection] = useState('up');
  // Value (percent or absolute price)
  const [value, setValue] = useState('');

  // Timing: 'now' | 'gradual'
  const [timing, setTiming] = useState('now');
  const [days, setDays] = useState('3');

  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  // Products in scope (must have a known current price)
  const scopedProducts = useMemo(() => {
    const base = products.filter(p => priceMap[p.offer_id]?.price);
    if (scope === 'selected' && selectedIds?.size > 0) {
      return base.filter(p => selectedIds.has(p.offer_id));
    }
    return base;
  }, [products, priceMap, scope, selectedIds]);

  // Compute target price for a product
  const getTargetPrice = (offerId) => {
    const current = parseFloat(priceMap[offerId]?.price || '0');
    if (!current || !value) return null;
    const v = parseFloat(value);
    if (isNaN(v) || v <= 0) return null;

    let target;
    if (changeType === 'percent') {
      target = direction === 'up'
        ? current * (1 + v / 100)
        : current * (1 - v / 100);
    } else {
      target = direction === 'up'
        ? current + v
        : current - v;
    }
    if (target <= 0) return null;
    return ceilPrice(target);
  };

  // Preview: items with computed target prices
  const previewItems = useMemo(() => {
    return scopedProducts
      .map(p => {
        const target = getTargetPrice(p.offer_id);
        if (!target) return null;
        const current = parseFloat(priceMap[p.offer_id]?.price || '0');
        return { offer_id: p.offer_id, name: p.name, current, target };
      })
      .filter(Boolean);
  // eslint-disable-next-line
  }, [scopedProducts, changeType, direction, value, priceMap]);

  // Schedule preview: per-day prices for first product (as illustration)
  const schedulePreview = useMemo(() => {
    const n = parseInt(days, 10);
    if (!previewItems.length || !n || n < 1) return null;
    const first = previewItems[0];
    return computeSchedulePrices(first.current, first.target, n);
  }, [previewItems, days]);

  const handleApply = async () => {
    if (!previewItems.length) { setError('Нет товаров для изменения'); return; }
    const n = parseInt(days, 10);
    if (timing === 'gradual' && (!n || n < 1 || n > MAX_DAYS)) {
      setError(`Количество дней: от 1 до ${MAX_DAYS}`);
      return;
    }
    setError('');
    setApplying(true);
    try {
      if (timing === 'now') {
        const prices = previewItems.map(it => ({
          offer_id: it.offer_id,
          price: String(it.target),
          old_price: String(it.current),
          name: it.name,
        }));
        await onApplyNow(prices);
      } else {
        const items = previewItems.map(it => ({
          offer_id: it.offer_id,
          name: it.name,
          current_price: String(it.current),
          target_price: String(it.target),
        }));
        await onSchedule(items, n);
      }
      onClose();
    } catch (e) {
      setError(e?.message || 'Ошибка при отправке');
    } finally {
      setApplying(false);
    }
  };

  const daysNum = parseInt(days, 10) || 1;

  return (
    <div className="pcm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pcm">
        <div className="pcm__header">
          <span className="pcm__title">Изменить цену</span>
          <button className="pcm__close" onClick={onClose}>✕</button>
        </div>

        <div className="pcm__body">
          {/* ── Scope ── */}
          <div className="pcm__row">
            <label className="pcm__label">Товары</label>
            <div className="pcm__options">
              <label className="pcm__radio">
                <input type="radio" value="all" checked={scope === 'all'}
                  onChange={() => setScope('all')} />
                Весь ассортимент ({products.filter(p => priceMap[p.offer_id]?.price).length} арт.)
              </label>
              {selectedIds?.size > 0 && (
                <label className="pcm__radio">
                  <input type="radio" value="selected" checked={scope === 'selected'}
                    onChange={() => setScope('selected')} />
                  Выбранные ({selectedIds.size} арт.)
                </label>
              )}
            </div>
          </div>

          {/* ── Change type ── */}
          <div className="pcm__row">
            <label className="pcm__label">Тип изменения</label>
            <div className="pcm__options pcm__options--inline">
              <label className="pcm__radio">
                <input type="radio" value="percent" checked={changeType === 'percent'}
                  onChange={() => setChangeType('percent')} />
                На %
              </label>
              <label className="pcm__radio">
                <input type="radio" value="absolute" checked={changeType === 'absolute'}
                  onChange={() => setChangeType('absolute')} />
                На ₽
              </label>
              <div className="pcm__dir-toggle">
                <button type="button"
                  className={`pcm__dir-btn${direction === 'up' ? ' pcm__dir-btn--on pcm__dir-btn--up' : ''}`}
                  onClick={() => setDirection('up')}>▲ Повысить</button>
                <button type="button"
                  className={`pcm__dir-btn${direction === 'down' ? ' pcm__dir-btn--on pcm__dir-btn--down' : ''}`}
                  onClick={() => setDirection('down')}>▼ Снизить</button>
              </div>
              <input
                type="number" min="0.01" step="0.01"
                className="pcm__value-input"
                placeholder={changeType === 'percent' ? '1' : '10'}
                value={value}
                onChange={e => setValue(e.target.value)}
              />
              <span className="pcm__unit">{changeType === 'percent' ? '%' : '₽'}</span>
            </div>
          </div>

          {/* ── Timing ── */}
          <div className="pcm__row">
            <label className="pcm__label">Применить</label>
            <div className="pcm__options">
              <label className="pcm__radio">
                <input type="radio" value="now" checked={timing === 'now'}
                  onChange={() => setTiming('now')} />
                Сразу
              </label>
              <label className="pcm__radio">
                <input type="radio" value="gradual" checked={timing === 'gradual'}
                  onChange={() => setTiming('gradual')} />
                Плавно за
                <input
                  type="number" min="1" max={MAX_DAYS}
                  className="pcm__days-input"
                  value={days}
                  onChange={e => setDays(e.target.value)}
                  onClick={e => { e.stopPropagation(); setTiming('gradual'); }}
                />
                дн.
              </label>
            </div>
          </div>

          {/* ── Schedule preview ── */}
          {timing === 'gradual' && schedulePreview && previewItems.length > 0 && (
            <div className="pcm__sched-preview">
              <div className="pcm__sched-label">
                Пример для «{previewItems[0].offer_id}» ({previewItems[0].current} ₽ → {previewItems[0].target} ₽):
              </div>
              <div className="pcm__sched-days">
                <div className="pcm__sched-day pcm__sched-day--today">
                  <span className="pcm__sday-date">Сейчас</span>
                  <span className="pcm__sday-price">{previewItems[0].current} ₽</span>
                </div>
                {schedulePreview.map((p, i) => (
                  <div key={i} className={`pcm__sched-day${i === schedulePreview.length - 1 ? ' pcm__sched-day--last' : ''}`}>
                    <span className="pcm__sday-date">{formatDate(i + 1)}</span>
                    <span className="pcm__sday-price">{p} ₽</span>
                  </div>
                ))}
              </div>
              <p className="pcm__sched-note">
                Каждый день в 10:00 МСК будет установлена конкретная цена (не %).
              </p>
            </div>
          )}

          {/* ── Products preview table ── */}
          {previewItems.length > 0 && (
            <div className="pcm__preview-wrap">
              <div className="pcm__preview-title">
                {previewItems.length} товаров{timing === 'now' ? ' — будут обновлены сразу' : ` — плавно за ${daysNum} дн.`}
              </div>
              <div className="pcm__table-wrap">
                <table className="pcm__table">
                  <thead>
                    <tr>
                      <th>Артикул</th>
                      <th>Название</th>
                      <th className="pcm__td-r">Текущая</th>
                      <th className="pcm__td-r">Целевая</th>
                      {timing === 'gradual' && daysNum > 1 && (
                        Array.from({ length: Math.min(daysNum, 5) }, (_, i) => (
                          <th key={i} className="pcm__td-r pcm__th-day">{formatDate(i + 1)}</th>
                        ))
                      )}
                      {timing === 'gradual' && daysNum > 5 && (
                        <th className="pcm__td-r pcm__th-day">…</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {previewItems.slice(0, 50).map(it => {
                      const dayPrices = timing === 'gradual' && daysNum > 1
                        ? computeSchedulePrices(it.current, it.target, daysNum)
                        : [];
                      return (
                        <tr key={it.offer_id}>
                          <td className="pcm__mono">{it.offer_id}</td>
                          <td className="pcm__trunc">{it.name || '—'}</td>
                          <td className="pcm__td-r">{it.current}</td>
                          <td className="pcm__td-r pcm__target">{it.target}</td>
                          {timing === 'gradual' && daysNum > 1 && dayPrices.slice(0, 5).map((p, i) => (
                            <td key={i} className="pcm__td-r pcm__day-cell">{p}</td>
                          ))}
                          {timing === 'gradual' && daysNum > 5 && (
                            <td className="pcm__td-r pcm__day-cell">…</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {previewItems.length > 50 && (
                  <p className="pcm__more">… и ещё {previewItems.length - 50} товаров</p>
                )}
              </div>
            </div>
          )}

          {previewItems.length === 0 && value && (
            <div className="pcm__empty">
              {!scopedProducts.length
                ? 'Нет товаров с известными ценами в выбранном диапазоне'
                : 'Введите значение изменения'}
            </div>
          )}

          {error && <div className="pcm__error">{error}</div>}
        </div>

        <div className="pcm__footer">
          <button className="btn btn--secondary" onClick={onClose} disabled={applying}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={handleApply}
            disabled={applying || !previewItems.length || !value}
          >
            {applying
              ? 'Отправка…'
              : timing === 'now'
                ? `Изменить сейчас (${previewItems.length})`
                : `Создать расписание (${previewItems.length} арт., ${daysNum} дн.)`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PriceChangeModal;
